import {type ReactNode, useCallback, useMemo, useState} from 'react'

import {type BoardInteraction, BoardInteractionContext, NO_DRAGGING_CARDS} from '../provider/contexts'
import type {BoardCardAddress} from '../types'
import {
  type Announcements,
  DndContext,
  type DragStartEvent,
  MouseSensor,
  pointerWithin,
  type ScreenReaderInstructions,
  TouchSensor,
  useSensor,
  useSensors,
} from './dnd-kit'
import {readBoardCardDragData} from './types'

/*
  A drag says nothing, and this is what makes that true rather than an accident.

  dnd-kit announces by default, and its wording interpolates the drag node id: dragging
  a card announces "Draggable item 4:todo:3:all:t1 was dropped.", which is the opaque
  composed key `provider/cell-key.ts` builds, in hard-coded English, on a board where
  every other string comes from the caller's `labels`. It announces from a monitor on the
  context rather than from a handler, so it speaks for every drag inside it -- including
  one `onDragStart` below read and declined, which is a drag the board has nothing to say
  about at all.

  Naming a card is what `getItemLabel` is for, and building an announcement out of it is
  https://github.com/github/views/issues/48, together with the keyboard drag there is
  wording to describe.

  So this is silence held on purpose until that lands, not a default nobody chose.
  `undefined` rather than an empty string, because dnd-kit skips a nullish announcement
  and puts anything else in the live region.
*/
const NOTHING_ANNOUNCED: Announcements = {
  onDragStart: () => undefined,
  onDragMove: () => undefined,
  onDragOver: () => undefined,
  onDragEnd: () => undefined,
  onDragCancel: () => undefined,
}

/*
  The same silence, for the description every draggable points at. dnd-kit's default
  reads "To pick up a draggable item, press the space bar. While dragging, use the arrow
  keys to move the item", which describes the keyboard drag the sensors below deliberately
  leave out. Offering it would put an instruction for a gesture that does nothing in front
  of exactly the reader least able to discover that.

  Empty rather than absent: `draggable` is a required string and dnd-kit draws the element
  either way, so this makes it an element with nothing in it. An `aria-describedby`
  pointing at one contributes no description, which is the state to be in until
  https://github.com/github/views/issues/48 supplies a real one.
*/
const NO_DRAG_INSTRUCTIONS: ScreenReaderInstructions = {draggable: ''}

/**
 * Holds the board's drag and publishes what it is carrying.
 *
 * Nothing registers as a drag source here: a card registers its own node in
 * `cells/use-board-card.ts`, and every cell registers itself as a drop target in
 * `cells/use-board-cell.ts`. What this layer settles is where the context lives and what
 * it says, so both arrive as hook calls rather than as a new prop at any call site.
 *
 * dnd-kit arrives through `drag/dnd-kit.ts`, which is this package's one door to the
 * library. Moving to a shared multi-dimensional wrapper later is a change to that file
 * and to nothing else.
 *
 * **This context covers everything a caller renders inside `BoardProvider`, which is
 * wider than the board.** A draggable binds to the nearest context above it, so a control
 * a caller renders beside the board body -- the composition `@github-ui/board` documents
 * -- drags on the board's terms rather than its own. `onDragStart` declining a payload it
 * cannot read keeps the board quiet about such a drag; it does not hand the drag back to
 * whoever owns it.
 *
 * Narrowing it means splitting where the state lives from where the context is mounted,
 * because the report below has to stay readable above the board's own markup. That is
 * https://github.com/github/views/issues/162, and it is worth noting the narrower shape
 * trades this mistake for the opposite one: a caller who leaves the boundary out gets a
 * board that never drags. Nothing composes a board that way today.
 *
 * **`children` is a prop so that a drag starting does not redraw the board.** Everything
 * below is built by `BoardProvider`, so the element is the same one across this
 * component's own state changes and React bails out of the subtree; only the cards
 * reading the interaction context wake up.
 *
 * That keeps the board's interaction state off the cards. It does not keep dnd-kit's off
 * them, and nothing here can: every card reads dnd-kit's own `InternalContext`, which is
 * memoised on the activator event, on `active`, on the active card's measured rect and on
 * `over`. The activator event goes in on `mousedown` and comes out on `mouseup`, so **a
 * press draws every card in the window again twice, whether or not it becomes a drag**,
 * and a drag that does start adds a few more as those other three settle. After that only
 * `over` changes, so a card re-renders per cell crossed rather than per pointer move, and
 * a draggable that is not the active one reads a null transform context and does not
 * re-render on movement at all. Virtualization bounds all of it to the drawn window. It is
 * the same cost the two dnd-kit boards already in this repository pay.
 */
export function BoardDragProvider({children}: {children: ReactNode}): ReactNode {
  const [draggingCards, setDraggingCards] = useState<readonly BoardCardAddress[]>(NO_DRAGGING_CARDS)

  /*
    Pointer input only, and not because the keyboard does not matter.

    dnd-kit's defaults are `PointerSensor` and `KeyboardSensor`, and the keyboard one
    activates on `Space` and `Enter` -- which is the card's own selection key. Both
    handlers would be an `onKeyDown` on the same card element, so the layer above would
    have to merge them, and merging settles nothing: dnd-kit refuses to activate on an
    event whose default was already prevented, so selection first means `Space` never
    picks a card up, and the sensor first means
    `selection/use-board-card-selection.ts` sees the prevented event and `Space` stops
    selecting, which is shipped behaviour regressing.

    `Space` cannot mean both, and which one it should mean is not this layer's to settle.
    Keyboard-driven moves are https://github.com/github/views/issues/49, and the
    `aria-roledescription` and `aria-describedby` that have to announce them are
    https://github.com/github/views/issues/48. They arrive together, which is what makes
    leaving them out here safe: there is no keyboard drag going unannounced.

    The activation distance matters on its own. Neither `PointerSensor` nor `MouseSensor`
    has a threshold by default, so a click that moves a pixel reads as a drag and the
    card's click-to-select becomes unreliable.
  */
  const sensors = useSensors(
    useSensor(MouseSensor, {activationConstraint: {distance: 4}}),
    useSensor(TouchSensor, {activationConstraint: {delay: 250, tolerance: 5}}),
  )

  const onDragStart = useCallback((event: DragStartEvent) => {
    const drag = readBoardCardDragData(event.active)
    // Something else in the caller's tree is being dragged. Not the board's to report.
    if (!drag) return

    const {columnKey, rowKey, itemId} = drag
    setDraggingCards([{columnKey, rowKey, itemId}])
  }, [])

  // The shared constant rather than a fresh `[]`, so the context value is one object for
  // the whole time nobody is dragging and a board at rest re-renders nothing.
  const onDragFinished = useCallback(() => {
    setDraggingCards(NO_DRAGGING_CARDS)
  }, [])

  const interaction = useMemo<BoardInteraction>(() => ({draggingCards}), [draggingCards])

  /*
    `DndContext` draws two visually hidden elements of its own, a live region and the
    description a draggable points at. `BoardProvider` renders no DOM, and both packages
    hold a caller to that: `@github-ui/board` asserts that a board puts exactly one
    element in the box around it, which is what a caller's own CSS selects on. So the two
    go to the end of the document instead. The description still resolves, since
    `aria-describedby` is by id and reads a hidden element.

    **The live region does not, inside a modal.** A `<dialog>` opened modally makes the
    rest of the document inert, and an inert subtree is out of the accessibility tree, so
    a region parked on the body announces nothing from a board inside one. Nothing is
    announced at all today, for the reason recorded above the announcements, so this
    settles nothing that https://github.com/github/views/issues/48 cannot revisit, and the
    answer there is probably a container taken from the board's own root.

    Read during render rather than in an effect. The document's body is the same element
    for the life of the page, so there is nothing here to go stale and nothing to react
    to, and setting it from an effect would cost the board a second render on mount for a
    value that was already known. Nothing is drawn before it: `DndContext` draws neither
    element until an effect of its own has run.
  */
  const accessibility = useMemo(
    () => ({
      announcements: NOTHING_ANNOUNCED,
      screenReaderInstructions: NO_DRAG_INSTRUCTIONS,
      // eslint-disable-next-line @github-ui/github-monorepo/no-stale-read-in-render
      container: typeof document === 'undefined' ? undefined : document.body,
    }),
    [],
  )

  return (
    /*
      `autoScroll={false}` is not optional. The board scrolls its own two containers with
      an axis lock and per-axis buffers, and two things scrolling one container fight each
      other. `contract.server.test.ts` fails any `DndContext` in this package that omits it.

      `collisionDetection` is set for the same kind of reason: the default is wrong for
      this board rather than merely different. dnd-kit's `rectIntersection` picks the
      target overlapping the dragged card's *translated* rect most, and that rect is the
      card's own, moved by the sensor's delta. On this board the card deliberately does
      not move -- a copy of it follows the pointer instead, for the reason recorded in
      `BoardDragOverlay` -- so a rect nobody can see would be choosing the destination
      while a reader aimed with the copy, and a card grabbed by one corner would aim from
      somewhere the pointer never was.

      `pointerWithin` is the strategy that matches what a reader is holding. It also
      answers with nothing at all when the pointer is between two cells or over a header,
      where an area strategy would still name whichever neighbour it overlapped most; on
      a board where every cell is a target and a drop is a move, naming a cell the pointer
      is not in is the more expensive mistake. Cells tile the band and nothing smaller
      registers, so wherever the pointer is over a cell at all there is exactly one
      answer.

      It takes the pointer from the sensor's own coordinates, which both sensors here
      supply. A keyboard sensor supplies none and would collide with nothing, so
      https://github.com/github/views/issues/49 has a strategy to revisit when it adds one.
    */
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      autoScroll={false}
      accessibility={accessibility}
      onDragStart={onDragStart}
      onDragEnd={onDragFinished}
      onDragCancel={onDragFinished}
    >
      <BoardInteractionContext value={interaction}>{children}</BoardInteractionContext>
    </DndContext>
  )
}
